import * as Sentry from '@sentry/react';

import type { CoverRead, UnreadableReason } from './certificatePrefill';
import { readCoverPeriod, type CoverPeriodReason } from './certificateDates';

/**
 * certificateRead — the one impure step between a staged certificate and a cover period.
 *
 * THE I/O LIVES HERE SO THE SEMANTICS CAN BE TESTED. `certificatePrefill` decides what a read does
 * to the fields, `certificateDates` decides what the text says, and `certificateOcrText` decides
 * what a scan's word boxes say; all three are pure and all three are pinned by node-env unit tests.
 * This file is the part that cannot be: it reaches for pdfjs and, on a scan, for a wasm OCR worker
 * — neither of which vitest's node env can load at all — so it holds as little judgement as
 * possible and ships on review.
 *
 * PDFJS IS IMPORTED LAZILY, AND ONLY AFTER THE CHEAP REFUSALS. It is the heaviest chunk in the
 * bundle and most of the reasons a read fails are knowable from the File alone — the same lazy
 * shape `CustomsDocumentsPanel` uses for the Bill of Entry (CustomsDocumentsPanel.tsx:119).
 *
 * AND OCR IS LAZIER STILL: IT IS NOT REACHED AT ALL UNLESS THE TEXT LAYER CAME BACK THIN. 39 of the
 * 41 certificates filed in production have a text layer and are answered in milliseconds without a
 * byte of tesseract being fetched. The two that do not — a scan printed through a PDF driver — are
 * the whole reason the second branch exists (owner's ruling, 2026-09-17: "OCR, then attested
 * fallback"), and they pay for it alone.
 *
 * THE OCR BRANCH OWNS ITS OWN FAILURES AND NOTHING PROPAGATES OUT OF IT. A page ceiling, a timer
 * and a dead worker are three different `UnreadableReason`s and all three are refusals rather than
 * exceptions: `rejectionFor` maps every one of them to the same 'unreadable' refusal the admin
 * already knows, which is what carries the attested fallback (`attestable`) onto them with no edit.
 *
 * EVERY END EMITS ONE BREADCRUMB, AND IT CARRIES NOTHING BUT THE OUTCOME. A prefill that silently
 * never fires looks exactly like a parser that is right every time, so each read leaves `status`,
 * `reason`, the parser's own `detail`, elapsed milliseconds and — since OCR — whether the scan
 * branch ran and what it cost behind it. Never the text, the dates or the filename, which are a
 * customer's document and have no business leaving the tab.
 *
 * `detail` IS THE ONLY WAY ANYONE LEARNS WHY. The screen collapses every unreadable end into one
 * sentence on purpose, so 'ordering-unknown' and 'contested' and 'no-dates-in-reach' are
 * indistinguishable to the admin and to this file — and they are the three that say whether the
 * parser is too shy, reading the wrong column, or looking at a layout nobody anticipated. All three
 * are enum values from a closed set: no text, no dates, no filename ever reaches Sentry.
 */

/** Above this, refuse before loading pdfjs: a certificate is a page or two, not a scanned book. */
const MAX_BYTES = 5 * 1024 * 1024;

/** A PDF text layer this thin is a scan or a cover sheet, not a certificate. */
const MIN_CHARS = 50;

/**
 * Counted over non-whitespace characters: `extractPdfText` joins every text item with a newline, so
 * a scan that yields nothing but page breaks still has a length. Asked of the OCR'd runs too, for
 * exactly the same reason — a page of stamps and rules comes back as a handful of stray marks.
 */
function thin(text: string): boolean {
    return text.replace(/\s/g, '').length < MIN_CHARS;
}

/** What a caller can say about a read it is asking for. */
export interface CertificateReadOptions {
    /**
     * The clock the parser's year-plausibility window follows. Callers that need a fixed one
     * (tests, a replay) hand one in; everything else takes the real one.
     */
    now?: Date;
    /**
     * MAY THIS READ SPEND AN OCR PASS? Default true, and false is the automatic on-file read
     * (`certificateOnFile.ts`), which fires on every policy view whether anyone asked a question
     * about the certificate or not. OCR is spent on a document an admin has just acted on — a file
     * they staged, or the `Check again` they pressed — and never on a page simply being looked at.
     */
    ocr?: boolean;
    /**
     * Called once if the read turns out to need OCR, so a surface can stop saying 'Reading the
     * certificate…' and say instead that the document is a scan and is being read here. A read that
     * never reaches the scan branch never calls it.
     */
    onPhase?: (phase: 'ocr') => void;
}

/** A read already taken of these exact bytes, and whether it was allowed to spend OCR. */
interface MemoisedRead {
    ocr: boolean;
    read: Promise<CoverRead>;
}

/**
 * ONE READ PER `File`, WHICH IS WHAT KEEPS A 20-SECOND CEILING FROM TURNING A PASS INTO A REFUSAL.
 *
 * Both save guards (`setup/data/insurance.ts:440` for contract setup, `insuranceCommit.ts`'s
 * `fileCertificate` for the policy dossier) re-ask the reader about the bytes they are about to
 * upload rather than trusting component state — a disabled control is a courtesy and state is not
 * evidence. On a text PDF that re-read costs milliseconds. On a scan it would cost a second OCR
 * pass, and a pass that came in at 18 seconds when the document was staged could come in at 21 when
 * Save is pressed: the admin would be told their certificate cannot be read by the very click that
 * was meant to file it. So the PROMISE is remembered, keyed on the `File` itself — every pick
 * produces exactly one File object, so Replace, Remove and Discard retire the entry for free and a
 * `WeakMap` lets the browser reclaim it with the document.
 *
 * A READ TAKEN WITHOUT OCR DOES NOT ANSWER FOR ONE ASKED WITH IT. That is the `Check again` case
 * exactly: the automatic on-file read passes `ocr: false`, and the click that follows it is an
 * admin asking for the expensive answer. The reverse is fine — a read that was allowed OCR is the
 * strictly better answer and is handed to a caller that would have settled for less.
 *
 * `now` IS NOT PART OF THE KEY. It moves the parser's year-plausibility window and nothing else,
 * and the two callers that pass one are a test and a replay, neither of which shares a `File` with
 * anything.
 */
const reads = new WeakMap<File, MemoisedRead>();

/**
 * Read the cover period off a certificate.
 *
 * `file` MAY HAVE COME THROUGH EITHER DOOR — staged for upload, or fetched back down out of storage
 * — and this function cannot tell and does not need to. What differs between them is `ocr`, and the
 * callers set it.
 */
export async function readCertificateCoverPeriod(
    file: File,
    options: CertificateReadOptions = {},
): Promise<CoverRead> {
    const wantsOcr = options.ocr !== false;
    const held = reads.get(file);
    if (held !== undefined && (held.ocr || !wantsOcr)) return held.read;

    const read = readOnce(file, options, wantsOcr);
    reads.set(file, { ocr: wantsOcr, read });

    /*
     * A REFUSAL THAT SAYS NOTHING ABOUT THE DOCUMENT IS NOT REMEMBERED. 'ocr-failed' is the one
     * ending that is about this app's own scheduling rather than about these bytes: a read
     * superseded by a newer staged document (`certificateOcr`'s generation guard), a lazy chunk
     * that would not load, a worker that would not come up. Remembered, it would answer the save
     * guards' deliberate re-read — and `Check again` — with a refusal the document never earned,
     * and the only way out would be Remove and re-pick. Every other ending IS a judgement about the
     * document, 'ocr-timeout' included: that one is the documented point of the memo, because a
     * second pass over the same page would take just as long as the first.
     *
     * The entry is retired only if it is still THIS read's, so a newer one is never clobbered.
     */
    void read.then(
        outcome => {
            if (outcome.status !== 'unreadable' || outcome.reason !== 'ocr-failed') return;
            if (reads.get(file)?.read === read) reads.delete(file);
        },
        () => undefined,
    );

    return read;
}

async function readOnce(
    file: File,
    options: CertificateReadOptions,
    wantsOcr: boolean,
): Promise<CoverRead> {
    const startedAt = Date.now();

    // The type is checked first and the extension second: a file dragged from some sources arrives
    // with an empty `type`, and a name ending `.pdf` is the only other evidence available.
    const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
    if (!isPdf) return unreadable('not-pdf', startedAt, false);
    if (file.size > MAX_BYTES) return unreadable('too-large', startedAt, false);

    let text: string;
    try {
        const { extractPdfText } = await import('@/lib/sbt-pdf');
        text = await extractPdfText(file);
    } catch {
        // Encrypted, corrupt, or not the document it says it is. The admin's next move is the same
        // as for every other unreadable end, so the cause is a breadcrumb and not a sentence.
        return unreadable('failed', startedAt, false);
    }

    /*
     * ── THE ONE BRANCH THIS FEATURE ADDS ────────────────────────────────────
     *
     * `ocrRan` IS BOUND HERE AND EXPLICITLY, rather than inferred later from a reason that happens
     * to start 'ocr-'. It is what puts `ocr: true` on a found read — which is what makes the screen
     * say the period came off a scan, and what makes `decideCertificate` insist on a PLATE match
     * before believing it — and a flag that important should be set by the code that did the thing.
     */
    let ocrRan = false;
    let ocrMs: number | undefined;

    if (thin(text)) {
        if (!wantsOcr) return unreadable('no-text', startedAt, false);

        ocrRan = true;
        const ocrStartedAt = Date.now();
        try {
            const { readCertificateByOcr } = await import('@/lib/certificateOcr');
            const outcome = await readCertificateByOcr(file, () => options.onPhase?.('ocr'));
            ocrMs = Date.now() - ocrStartedAt;
            if (outcome.status === 'refused') {
                return unreadable(outcome.reason, startedAt, true, undefined, ocrMs);
            }
            text = outcome.text;
        } catch {
            // The chunk would not load, the worker would not start, the browser gave no canvas.
            // Every one of them is the same fact to an admin: nothing here could read the page.
            ocrMs = Date.now() - ocrStartedAt;
            return unreadable('ocr-failed', startedAt, true, undefined, ocrMs);
        }

        // The pass ran and produced a page of marks rather than a page of words. This is its own
        // reason rather than 'no-text' so the breadcrumb says which of the two failed.
        if (thin(text)) return unreadable('ocr-no-text', startedAt, true, undefined, ocrMs);
    }

    const found = readCoverPeriod(text, options.now);
    if (found.status === 'found') {
        breadcrumb('found', undefined, startedAt, undefined, ocrRan, ocrMs);
        // THE IDENTIFIERS RIDE ALONG AND ARE NOT JUDGED HERE. `decideCertificate` compares them
        // against the contract's vehicle, because this file has no idea which contract it is
        // reading for — and because the comparison is a pure rule that belongs under test.
        return {
            status: 'found',
            startsOn: found.startsOn!,
            expiresOn: found.expiresOn!,
            ...(found.vehicle ? { vehicle: found.vehicle } : {}),
            // THE PROVENANCE TRAVELS WITH THE PERIOD. A read off a scan is believed on a narrower
            // rule than a read off a text layer and says so on the screen, and neither is possible
            // unless the read itself remembers where it came from.
            ...(ocrRan ? { ocr: true as const } : {}),
        };
    }

    // 'none' and 'ambiguous' both mean the same thing to the admin — the document has to be read by
    // eye — so the status is what the screen gets and the parser's own `reason` rides along as the
    // breadcrumb's `detail`, which is the only place the two are ever told apart.
    return unreadable(found.status, startedAt, ocrRan, found.reason, ocrMs);
}

/**
 * `detail` NOW RIDES ON THE READ AS WELL AS ON THE BREADCRUMB, for exactly one distinction.
 * Every unreadable end is one refusal to the admin since the 2026-09-12 ruling, but
 * 'not-a-statutory-certificate' is the parser saying the file is the wrong KIND of document rather
 * than an unreadable one — a different sentence, and the only one of the reasons that changes
 * what there is to do about it. `decideCertificate` is what reads it; nothing else does.
 *
 * AND `ocr` NOW RIDES ON THE READ AS WELL, WHICH IT ALREADY HAD TO KNOW TO WRITE THE BREADCRUMB.
 * A verdict given over a text layer is given over the insurer's own words; the same verdict over
 * OCR output is given over this app's guess at some pixels, and `attestable` divides them. The
 * flag was already a parameter here for the breadcrumb's sake, so the read gains the same fact its
 * `found` sibling has carried since the scan branch landed rather than a second source for it.
 */
function unreadable(
    reason: UnreadableReason,
    startedAt: number,
    ocr: boolean,
    detail?: CoverPeriodReason,
    ocrMs?: number,
): CoverRead {
    breadcrumb('unreadable', reason, startedAt, detail, ocr, ocrMs);
    return {
        status: 'unreadable',
        reason,
        ...(detail ? { detail } : {}),
        ...(ocr ? { ocr: true as const } : {}),
    };
}

/**
 * One breadcrumb per read, carrying the outcome and nothing else.
 *
 * `ocr` AND `ocrMs` ARE THE ONLY WAY ANYONE LEARNS WHAT THE SCAN BRANCH COSTS IN THE FIELD. The
 * 20-second ceiling and the 300 dpi render were both set against ONE document on one machine; what
 * an admin's laptop does with a three-page scan over a Ghanaian connection is not something this
 * repository can measure, and a feature that quietly times out would otherwise be indistinguishable
 * from one nobody is using. Both are scalars from the same closed shape as the rest of this record.
 *
 * Sentry is initialised once at boot (src/sentry.ts) and is disabled without a DSN, in which case
 * this is a no-op — which is the correct behaviour locally and the reason there is no guard.
 */
function breadcrumb(
    status: 'found' | 'unreadable',
    reason: UnreadableReason | undefined,
    startedAt: number,
    detail: CoverPeriodReason | undefined,
    ocr: boolean,
    ocrMs: number | undefined,
): void {
    Sentry.addBreadcrumb({
        category: 'certificate.read',
        level: status === 'found' ? 'info' : 'warning',
        message: 'cover period read',
        data: { status, reason, detail, ms: Date.now() - startedAt, ocr, ocrMs },
    });
}
